import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import useEventCallback from './useEventCallback'

/**
 * A prefix to identify session and local storage keys saved using
 * the storage hooks in this application.
 */
const STORAGE_KEYS_PREFIX = 'bonhams-web_'

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string): T | undefined {
  try {
    return value === 'undefined' ? undefined : (JSON.parse(value) as T)
  } catch {
    // eslint-disable-next-line no-console
    console.warn('parsing error on', { value })
    return undefined
  }
}

export type SetValue<T> = Dispatch<SetStateAction<T>>

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, SetValue<T>, () => void] {
  const storageKey = STORAGE_KEYS_PREFIX + key

  const readValue = useCallback((): T => {
    try {
      const item =
        typeof window !== 'undefined' && window.localStorage.getItem(storageKey)

      return item ? (parseJSON(item) as T) : initialValue
    } catch {
      return initialValue
    }
  }, [initialValue, storageKey])

  const [storedValue, setStoredValue] = useState<T>(readValue)

  const setValue: SetValue<T> = useEventCallback((value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value

      // Save to local storage
      window.localStorage.setItem(storageKey, JSON.stringify(newValue))

      // Save state
      setStoredValue(newValue)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error setting localStorage key “${storageKey}”:`, error)
    }
  })

  const removeValue = useEventCallback(() =>
    window.localStorage.removeItem(storageKey)
  )

  useEffect(() => {
    setStoredValue(readValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [storedValue, setValue, removeValue]
}
